/**
 * Odemis UI · ficha de producto
 * Galería y visor ampliado, cantidad, variantes, "Comprar ahora",
 * barra fija en mobile, pestañas/acordeones y ficha técnica imprimible.
 */
(() => {
  'use strict';

  // Las secciones de la ficha cargan este archivo cada una: se ejecuta una sola vez.
  if (window.odProductLoaded) return;
  window.odProductLoaded = true;

  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const define = (name, ctor) => !customElements.get(name) && customElements.define(name, ctor);
  const scrollBehavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  const formatMoney = (cents, format) => {
    const amount = Number(cents || 0) / 100;
    const withDelimiters = (decimals, thousands, decimal) => {
      const [int, frac] = amount.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return (format || 'S/ {{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withDelimiters(0, ',', '.');
      if (key === 'amount_with_comma_separator') return withDelimiters(2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return withDelimiters(0, '.', ',');
      return withDelimiters(2, ',', '.');
    });
  };

  /* -------------------------------------------------------------------
     Galería
     ------------------------------------------------------------------- */
  class OdGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-od-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-od-thumb]')];
      this.dots = [...this.querySelectorAll('.od-pdp__dot')];
      this.current = this.querySelector('[data-od-gallery-current]');
      this.index = 0;

      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      }, { passive: true });
      // Si la persona toma el control a mitad de un desplazamiento animado, manda su gesto.
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
        this.track.addEventListener(type, () => (this.target = null), { passive: true })
      );

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.odThumb))));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(Math.min(this.index + 1, this.slides.length - 1));
        else if (event.key === 'ArrowLeft') this.goTo(Math.max(this.index - 1, 0));
        else return;
        event.preventDefault();
      });

      this.setupZoom();
    }

    sync() {
      // Durante el desplazamiento animado hacia la imagen elegida ignoramos las
      // posiciones intermedias para que miniaturas y puntos no parpadeen.
      if (this.target != null) {
        if (Math.abs(this.track.scrollLeft - this.offsetOf(this.target)) > 2) return;
        this.target = null;
      }
      const width = this.track.clientWidth || 1;
      const index = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
      if (index === this.index) return;
      this.index = index;
      this.render();
    }

    render() {
      if (this.current) this.current.textContent = String(this.index + 1);
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
      this.slides.forEach((slide, i) => {
        if (i !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      const activeThumb = this.thumbs[this.index];
      if (activeThumb && activeThumb.parentElement.scrollWidth > activeThumb.parentElement.clientWidth) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: scrollBehavior() });
      }
    }

    offsetOf(index) {
      return this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    }

    goTo(index, behavior = scrollBehavior()) {
      if (!this.slides[index]) return;
      clearTimeout(this.targetTimer);
      this.target = behavior === 'smooth' ? index : null;
      if (this.target != null) {
        this.targetTimer = setTimeout(() => {
          this.target = null;
          this.sync();
        }, 1000);
      }
      this.track.scrollTo({ left: this.offsetOf(index), behavior });
      this.index = index;
      this.render();
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index);
    }

    setupZoom() {
      this.zoom = this.querySelector('[data-od-zoom]');
      if (!this.zoom) return;
      this.zoomTrack = this.zoom.querySelector('[data-od-zoom-track]');
      this.zoomCurrent = this.zoom.querySelector('[data-od-zoom-current]');
      this.zoomSlides = [...this.zoomTrack.children];
      this.zoomIndex = 0;

      this.querySelectorAll('[data-od-zoom-open]').forEach((button) =>
        button.addEventListener('click', () => {
          const value = button.dataset.odZoomOpen;
          this.zoomOpener = button;
          this.openZoom(value === '' ? this.index : Number(value));
        })
      );
      this.zoom.querySelector('[data-od-zoom-close]')?.addEventListener('click', () => this.zoom.close());
      this.zoom.querySelectorAll('[data-od-zoom-step]').forEach((button) =>
        button.addEventListener('click', () => this.stepZoom(Number(button.dataset.odZoomStep)))
      );
      this.zoom.addEventListener('click', (event) => {
        if (event.target === this.zoom) this.zoom.close();
      });
      this.zoom.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.stepZoom(1);
        if (event.key === 'ArrowLeft') this.stepZoom(-1);
      });
      this.zoom.addEventListener('close', () => {
        document.documentElement.classList.remove('od-lock');
        clearTimeout(this.zoomSettle);
        this.goTo(this.zoomIndex, 'auto');
        // El foco vuelve a un control visible: el botón general o el de la imagen actual.
        const opener = this.zoomOpener;
        const target = opener && opener.dataset.odZoomOpen === '' && opener.offsetParent
          ? opener
          : this.slides[this.zoomIndex]?.querySelector('[data-od-zoom-open]');
        target?.focus({ preventScroll: true });
      });
      // Al cerrarse, el visor pierde su caja y el navegador informa un scroll a 0:
      // solo tomamos la posición mientras está abierto y cuando el gesto termina.
      this.zoomTrack.addEventListener('scroll', () => {
        if (!this.zoom.open || !this.zoomTrack.clientWidth) return;
        clearTimeout(this.zoomSettle);
        this.zoomSettle = setTimeout(() => {
          if (!this.zoom.open || !this.zoomTrack.clientWidth) return;
          this.setZoomIndex(Math.round(this.zoomTrack.scrollLeft / this.zoomTrack.clientWidth));
        }, 120);
      }, { passive: true });
    }

    setZoomIndex(index) {
      this.zoomIndex = Math.min(this.zoomSlides.length - 1, Math.max(0, index));
      if (this.zoomCurrent) this.zoomCurrent.textContent = String(this.zoomIndex + 1);
    }

    openZoom(index) {
      if (!this.zoom) return;
      this.zoomSlides.forEach((slide) => slide.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager')));
      this.zoom.showModal();
      document.documentElement.classList.add('od-lock');
      this.setZoomIndex(index);
      // El navegador puede restaurar la posición de la vez anterior: la fijamos ahora y en el siguiente cuadro.
      const place = () => (this.zoomTrack.scrollLeft = this.zoomTrack.clientWidth * this.zoomIndex);
      place();
      requestAnimationFrame(place);
    }

    stepZoom(direction) {
      const count = this.zoomSlides.length;
      clearTimeout(this.zoomSettle);
      this.setZoomIndex((this.zoomIndex + direction + count) % count);
      this.zoomTrack.scrollTo({ left: this.zoomTrack.clientWidth * this.zoomIndex, behavior: scrollBehavior() });
    }
  }
  define('od-gallery', OdGallery);

  /* -------------------------------------------------------------------
     Pestañas (desktop) y acordeones (mobile) del detalle
     ------------------------------------------------------------------- */
  class OdTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tabs = [...this.querySelectorAll('[data-od-tab]')];
      this.items = [...this.querySelectorAll('details[data-od-panel]')];
      this.active = this.dataset.defaultTab;
      if (!this.items.some((item) => item.dataset.odPanel === this.active)) this.active = this.items[0]?.dataset.odPanel;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.odTab));
        tab.addEventListener('keydown', (event) => {
          const index = this.tabs.indexOf(tab);
          let target = null;
          if (event.key === 'ArrowRight') target = this.tabs[(index + 1) % this.tabs.length];
          if (event.key === 'ArrowLeft') target = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          if (!target) return;
          event.preventDefault();
          target.focus();
          this.select(target.dataset.odTab);
        });
      });

      // En desktop el resumen del acordeón no se usa: evitamos que lo cierre.
      this.items.forEach((item) =>
        item.querySelector('summary')?.addEventListener('click', (event) => {
          if (mqDesktop.matches) event.preventDefault();
        })
      );

      this.onBreakpoint = () => this.apply();
      mqDesktop.addEventListener('change', this.onBreakpoint);
      this.apply();
    }

    disconnectedCallback() {
      mqDesktop.removeEventListener('change', this.onBreakpoint);
      this.ready = false;
    }

    select(name) {
      this.active = name;
      this.apply();
    }

    apply() {
      const desktop = mqDesktop.matches;
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.odTab === this.active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (desktop) {
        this.items.forEach((item) => (item.open = item.dataset.odPanel === this.active));
        if (!this.keptOpen) {
          this.keptOpen = true;
          this.querySelectorAll('details[data-od-keep-open]').forEach((item) => (item.open = true));
        }
      }
    }

    openPanel(name) {
      this.active = name;
      const item = this.items.find((el) => el.dataset.odPanel === name);
      if (item) item.open = true;
      this.apply();
      (mqDesktop.matches ? this : item || this).scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    }
  }
  define('od-tabs', OdTabs);

  /* -------------------------------------------------------------------
     Bloque de compra
     ------------------------------------------------------------------- */
  const setupProduct = (section) => {
    if (section.odReady) return;
    section.odReady = true;

    const moneyFormat = section.dataset.moneyFormat;
    const lowStock = Number(section.dataset.lowStock || 3);
    const stockCap = Number(section.dataset.stockCap || 10);
    const dataEl = section.querySelector('[data-od-product-json]');
    const data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] };
    const form = section.querySelector('form[data-type="add-to-cart-form"]');
    const variantInput = section.querySelector('[data-od-variant-input]');
    const qtyInput = section.querySelector('.od-qty__input');
    const addButton = section.querySelector('[data-od-add]');
    const buyNow = section.querySelector('[data-od-buy-now]');
    const sticky = section.querySelector('[data-od-sticky]');
    const gallery = section.querySelector('od-gallery');

    /* Cantidad */
    const clampQty = () => {
      if (!qtyInput) return;
      const max = Number(qtyInput.max || 99);
      const value = Math.min(max, Math.max(1, parseInt(qtyInput.value, 10) || 1));
      qtyInput.value = String(value);
    };
    section.querySelectorAll('[data-od-qty-step]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!qtyInput) return;
        qtyInput.value = String((parseInt(qtyInput.value, 10) || 1) + Number(button.dataset.odQtyStep));
        clampQty();
      })
    );
    qtyInput?.addEventListener('change', clampQty);

    /* Variantes */
    const optionInputs = [...section.querySelectorAll('[data-od-option]')];
    const stockInfo = (variant) => {
      if (!variant || !variant.available) return ['out', 'Agotado', 1];
      if (variant.tracked && variant.quantity <= lowStock) {
        return ['low', variant.quantity === 1 ? '¡Última unidad disponible!' : `¡Últimas ${variant.quantity} unidades!`, Math.max(1, variant.quantity)];
      }
      if (variant.tracked && variant.quantity > stockCap) return ['in', `Disponible: más de ${stockCap} unidades`, variant.quantity];
      if (variant.tracked) return ['in', `Disponible: ${variant.quantity} unidades`, variant.quantity];
      return ['in', 'Disponible', 99];
    };

    const renderVariant = (variant) => {
      section.querySelectorAll('[data-od-price]').forEach((el) => {
        el.textContent = variant && variant.price > 0 ? formatMoney(variant.price, moneyFormat) : data.ask_label;
      });
      const onSale = variant && variant.compare_at_price > variant.price;
      const meta = section.querySelector('[data-od-price-meta]');
      if (meta) meta.hidden = !onSale;
      const badge = section.querySelector('[data-od-discount]');
      if (onSale) {
        section.querySelector('[data-od-compare]').textContent = formatMoney(variant.compare_at_price, moneyFormat);
        section.querySelector('[data-od-save]').textContent = formatMoney(variant.compare_at_price - variant.price, moneyFormat);
        if (badge) badge.textContent = `-${Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)}%`;
      }
      if (badge) badge.hidden = !onSale;

      const [state, text, max] = stockInfo(variant);
      const stock = section.querySelector('[data-od-stock]');
      if (stock) {
        stock.className = `od-pdp__stock od-pdp__stock--${state}`;
        stock.querySelector('[data-od-stock-text]').textContent = variant ? text : 'Combinación no disponible';
      }
      if (qtyInput) {
        qtyInput.max = String(max);
        clampQty();
      }

      document.querySelectorAll('[data-od-sku]').forEach((el) => (el.textContent = (variant && variant.sku) || ''));
      const available = Boolean(variant && variant.available);
      [addButton, buyNow, sticky?.querySelector('[data-od-sticky-add]')].forEach((button) => {
        if (button) button.disabled = !available;
      });
      section.querySelectorAll('[data-od-add-text]').forEach((el) => {
        el.textContent = available ? data.add_label : variant ? 'Agotado' : 'No disponible';
        // product-form.js restaura este texto al terminar de agregar
        el.dataset.originalText = el.textContent;
      });

      if (variant) {
        variantInput.value = variant.id;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.media_id && gallery) gallery.goToMedia(variant.media_id);
      }
    };

    optionInputs.forEach((input) =>
      input.addEventListener('change', () => {
        const selected = [];
        section.querySelectorAll('[data-od-options] fieldset').forEach((fieldset, index) => {
          const checked = fieldset.querySelector('input:checked');
          selected[index] = checked ? checked.value : null;
          const label = section.querySelector(`[data-od-option-label="${index}"]`);
          if (label && checked) label.textContent = checked.value;
        });
        const variant = data.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        renderVariant(variant);
      })
    );

    /* Comprar ahora: agrega al carrito y va directo al pago */
    buyNow?.addEventListener('click', async () => {
      if (!form || buyNow.disabled) return;
      buyNow.classList.add('loading');
      buyNow.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'No se pudo agregar el producto.');
        }
        window.location.href = `${root}checkout`;
      } catch (error) {
        const wrapper = section.querySelector('.product-form__error-message-wrapper');
        if (wrapper) {
          wrapper.hidden = false;
          wrapper.querySelector('.product-form__error-message').textContent = error.message;
        }
        buyNow.classList.remove('loading');
        buyNow.removeAttribute('aria-busy');
      }
    });

    /* Barra fija en mobile */
    if (sticky && addButton) {
      document.body.classList.add('od-has-sticky-buy');
      // El área observada se extiende hacia abajo: el botón solo "sale" cuando queda por encima
      // de la pantalla, aunque se llegue de un salto (ancla, volver atrás) sin verlo antes.
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        sticky.querySelector('[data-od-sticky-add]').tabIndex = show ? 0 : -1;
      }, { rootMargin: '0px 0px 100000px 0px' });
      observer.observe(addButton);
      sticky.querySelector('[data-od-sticky-add]').addEventListener('click', () => addButton.click());
    }

    /* Enlace a la ficha técnica */
    section.querySelectorAll('[data-od-goto-tab]').forEach((link) =>
      link.addEventListener('click', (event) => {
        const tabs = document.querySelector('od-tabs');
        if (!tabs) return;
        event.preventDefault();
        tabs.openPanel(link.dataset.odGotoTab);
      })
    );
  };

  /* -------------------------------------------------------------------
     Ficha técnica imprimible (cuando no hay PDF de la marca)
     ------------------------------------------------------------------- */
  const escapeHTML = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  // Shopify entrega las imágenes como "//cdn…": la ficha se abre en otra ventana, así que van absolutas.
  const absoluteURL = (url) => {
    try {
      return url ? new URL(url, window.location.href).href : '';
    } catch (error) {
      return '';
    }
  };

  const buildSheet = (data, print) => {
    const date = new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });
    const logo = absoluteURL(data.logo);
    const image = absoluteURL(data.image);
    const rows = (data.specs || [])
      .map(([label, value]) => `<tr><th>${escapeHTML(label)}</th><td>${escapeHTML(value)}</td></tr>`)
      .join('');
    return `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>Ficha técnica · ${escapeHTML(data.title)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Inter, 'Segoe UI', Arial, sans-serif; color: #18131f; }
  .sheet { max-width: 900px; margin: 0 auto; padding: 24px; }
  header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-bottom: 16px; border-bottom: 3px solid #520a86; }
  header img { height: 40px; width: auto; background: #520a86; padding: 6px 10px; border-radius: 8px; }
  header .meta { text-align: right; font-size: 12px; color: #716a79; }
  h1 { margin: 22px 0 6px; font-size: 24px; line-height: 1.25; }
  .codes { font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: #716a79; }
  .top { display: grid; grid-template-columns: 260px 1fr; gap: 24px; margin-top: 20px; align-items: start; }
  .photo { background: #f6f5f8; border-radius: 14px; padding: 16px; }
  .photo img { width: 100%; height: auto; mix-blend-mode: multiply; }
  .price { background: #f7f3fd; border-radius: 14px; padding: 16px 18px; }
  .price small { display: block; font-size: 11px; font-weight: 800; letter-spacing: .06em; color: #520a86; }
  .price strong { display: block; font-size: 30px; color: #520a86; margin-top: 4px; }
  .price s { color: #716a79; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin-top: 24px; font-size: 13px; }
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid #e7e4ec; vertical-align: top; }
  th { width: 34%; background: #f6f5f8; color: #49424f; }
  footer { margin-top: 24px; font-size: 11px; color: #716a79; }
  @media print { .sheet { padding: 0; } }
</style></head><body><div class="sheet">
<header>${logo ? `<img src="${escapeHTML(logo)}" alt="${escapeHTML(data.shop)}">` : `<strong>${escapeHTML(data.shop)}</strong>`}
<div class="meta">Ficha técnica<br>${escapeHTML(date)}</div></header>
<h1>${escapeHTML(data.title)}</h1>
<div class="codes">${data.sku ? `SKU ${escapeHTML(data.sku)}` : ''}${data.sku && data.model ? ' · ' : ''}${data.model ? `Modelo ${escapeHTML(data.model)}` : ''}</div>
<div class="top">
  <div class="photo">${image ? `<img src="${escapeHTML(image)}" alt="">` : ''}</div>
  <div class="price"><small>PRECIO ONLINE</small><strong>${escapeHTML(data.price)}</strong>${data.compare ? `<s>Precio regular ${escapeHTML(data.compare)}</s>` : ''}</div>
</div>
${rows ? `<table>${rows}</table>` : ''}
<footer>Precios y stock sujetos a cambio sin previo aviso. Más información en ${escapeHTML(data.url)}</footer>
</div>${print ? '<script>window.addEventListener("load",()=>setTimeout(()=>window.print(),300));<\/script>' : ''}</body></html>`;
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-od-sheet]');
    if (!button) return;
    const dataEl = document.querySelector('[data-od-sheet-data]');
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const html = buildSheet(data, button.dataset.odSheet === 'print');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  });

  /* -------------------------------------------------------------------
     Entregas con hora de corte (hora de Lima, lunes a viernes)
     ------------------------------------------------------------------- */
  const limaNow = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Lima',
      weekday: 'short',
      hour: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type).value;
    return { day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')), hour: Number(get('hour')) };
  };

  const updateDeliveryBadges = () => {
    const items = document.querySelectorAll('[data-od-cutoff]');
    if (!items.length) return;
    const { day, hour } = limaNow();
    const weekday = day >= 1 && day <= 5;
    items.forEach((item) => {
      const badge = item.querySelector('[data-od-delivery-badge]');
      if (!badge) return;
      if (!item.dataset.odBadgeToday) item.dataset.odBadgeToday = badge.textContent;
      const off = !weekday || hour >= Number(item.dataset.odCutoff);
      let text = item.dataset.odBadgeToday;
      if (off) {
        const nextIsMonday = day === 5 || day === 6 || day === 0;
        text = (nextIsMonday ? item.dataset.odBadgeMonday : item.dataset.odBadgeNext) || '';
      }
      badge.textContent = text;
      badge.hidden = !text;
      badge.classList.toggle('is-off', off);
    });
  };

  const init = () => {
    document.querySelectorAll('[data-od-product]').forEach(setupProduct);
    updateDeliveryBadges();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
